import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';

import { ApiError, api, type AuthUser, type ConsoleLocale, type Role } from '@/lib/api';
import { DEFAULT_LOCALE, LocaleProvider } from '@/lib/i18n';
import { session as sessionStore } from '@/lib/session-store';

/**
 * The console session — real as of Phase 4 (build/06 §5).
 *
 * This was a stub that accepted any email and checked nothing. It now calls
 * `POST /auth/login` and derives the user from the server's response rather than
 * from the shape of the address typed in. The context shape is unchanged, which
 * is what the Phase-0 stub promised.
 *
 * **IT NO LONGER KEEPS A TOKEN PAIR** (todo §5). The access and refresh tokens
 * are `httpOnly` cookies set by the login response; this provider keeps only a
 * display hint, and `signOut` therefore has to reach the server to actually end
 * anything. See `lib/session-store.ts`.
 *
 * WHAT THIS STILL IS NOT: authorisation. The role here decides what the console
 * shows; it decides nothing about what the API permits. Every protected route
 * enforces `requireAuth`/`requireRole` server-side, and a user who edits their
 * stored session to say `admin` gains exactly nothing — the token is signed and
 * the server reads the claim from the token, never from the client.
 */

export interface SessionUser {
  email: string;
  name: string;
  /**
   * The RAW role, not a display label.
   *
   * It used to be `'Administrator' | 'Analyst'` — English, computed here, and
   * therefore untranslatable at the point it was rendered. A session carries
   * facts; turning one into words is the view's job (`role.admin` in the
   * catalogue).
   */
  role: Role;
  initials: string;
}

interface SessionContextValue {
  user: SessionUser | null;
  /** Throws `ApiError` on bad credentials so the form can show the reason. */
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** True while a sign-in is in flight, so the form can disable itself. */
  pending: boolean;

  /**
   * The operator's console language (Workstream H1).
   *
   * It lives here rather than in a store of its own because it is a fact about
   * *who is signed in*: it arrives with the profile, and it must reset when the
   * session does — otherwise the next person to sign in on this machine inherits
   * the last one's language, which is precisely the bug device-local storage
   * would have introduced.
   */
  locale: string;
  /** Persists the choice to the account, then applies it. Throws on failure. */
  setLocale: (locale: ConsoleLocale) => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * `noor.alharbi@etcc.ae` → `NA`. Presentation only.
 *
 * Punctuation is stripped from the front of each word before its first
 * character is taken. `Admin (fixture)` rendered as **`A(`** in the topbar
 * otherwise — found on screen once R1's fixture accounts existed, because until
 * then no display name in this system had a bracketed word in it.
 */
function initialsOf(name: string, email: string): string {
  const source = name.trim() || email.split('@')[0] || 'A';
  const words = source
    .split(/[\s._-]+/)
    .map((word) => word.replace(/^[^\p{L}\p{N}]+/u, ''))
    .filter(Boolean);
  return (words.slice(0, 2).map((w) => w.charAt(0)).join('') || 'A').toUpperCase();
}

function toSessionUser(user: AuthUser): SessionUser {
  const name = user.name?.trim() || user.email.split('@')[0] || 'Administrator';
  return {
    email: user.email,
    name,
    // The role now comes from the signed token's claims, not from a guess.
    role: user.role,
    initials: initialsOf(name, user.email),
  };
}

/**
 * Does this look like an operator profile?
 *
 * Guarded because the discovery call below runs on EVERY mount, including for a
 * visitor who has never signed in — so it must be able to tell a real profile
 * from whatever a misconfigured proxy or a test double answers with. Believing a
 * shapeless 200 would sign someone into a console with no name and no role.
 */
function isProfile(value: unknown): value is AuthUser & { locale?: string } {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AuthUser>;
  return (
    typeof candidate.email === 'string'
    && typeof candidate.userId === 'string'
    && (candidate.role === 'admin' || candidate.role === 'psychometrician' || candidate.role === 'analyst')
  );
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => {
    const stored = sessionStore.user();
    return stored ? toSessionUser(stored) : null;
  });
  const [pending, setPending] = useState(false);
  const [locale, setLocaleState] = useState<string>(DEFAULT_LOCALE);

  /**
   * Read the profile whenever there is a session, because the language is NOT in
   * the token — deliberately, since a token is presented on every request and
   * this is read once.
   *
   * A failure here is not a session failure. An operator whose profile call was
   * refused by a flaky network is signed in; they simply read the console in the
   * default language until the next load. Treating it as fatal would turn a
   * cosmetic preference into a sign-in outage.
   */
  useEffect(() => {
    if (!user) {
      setLocaleState(DEFAULT_LOCALE);
      return;
    }
    let cancelled = false;
    void api
      .me()
      .then((profile) => {
        if (!cancelled && profile?.locale) setLocaleState(profile.locale);
      })
      .catch(() => {
        // Deliberately silent: see above.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  /**
   * DISCOVERY — a tab with no stored hint may still hold a live session cookie.
   *
   * The hint lives in `sessionStorage`, which is per-tab; the session cookie is
   * per-browser. So opening the console in a second tab, or restoring one, gives
   * a signed-in browser and a provider that knows nothing about it. Asking the
   * server is the only way to find out, and it is also the only authoritative
   * answer — the hint is a cache of it.
   *
   * Runs once while there is no user (`user` is the dependency and the guard
   * returns immediately once one exists, so there is no loop), and stays silent
   * on failure: a 401 here is the ordinary case for a visitor who is simply not
   * signed in, and a network blip must not be reported as a session.
   */
  useEffect(() => {
    if (user) return;
    let cancelled = false;
    void api
      .me()
      .then((profile) => {
        if (cancelled || !isProfile(profile)) return;
        sessionStore.setUser(profile);
        setUser(toSessionUser(profile));
        if (profile.locale) setLocaleState(profile.locale);
      })
      .catch(() => {
        // Not signed in, or the API is unreachable. Either way: stay on login.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const signIn = useCallback(async (email: string, password: string) => {
    setPending(true);
    try {
      // The response carries NO token — the credentials arrived as `httpOnly`
      // cookies on the same response, which is the whole of todo §5. All that is
      // kept here is who to render.
      const { user: signedIn } = await api.login(email, password);
      sessionStore.setUser(signedIn);
      setUser(toSessionUser(signedIn));
    } finally {
      setPending(false);
    }
  }, []);

  /**
   * Persist first, then apply.
   *
   * The other order — switch the UI, then save — shows a console that changed
   * language and silently reverts on the next sign-in, which is the worst of
   * both: the operator believes the setting works and it does not.
   */
  const setLocale = useCallback(async (next: ConsoleLocale) => {
    const profile = await api.setLocale(next);
    setLocaleState(profile.locale);
  }, []);

  /**
   * Sign out — SERVER FIRST, and the order is the change.
   *
   * It used to clear local storage first, on the reasoning that "the user asked
   * to be signed out, and that must happen whether or not the network agrees".
   * That was right when the credential was a value this code held: dropping it
   * ended the session locally by definition. It is now an `httpOnly` cookie the
   * console cannot touch, so clearing first would leave a fully live session
   * behind a console that merely looked signed out — and the discovery effect
   * above would then re-adopt it on the next mount.
   *
   * So: ask the server to revoke and clear, then forget locally in a `finally`
   * so a failure still returns the operator to the login screen. A failed logout
   * now genuinely means "that session may still be live", which is a thing worth
   * being able to tell.
   */
  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch (err) {
      // An already-expired or unreachable session still ends locally.
      if (!(err instanceof ApiError)) throw err;
    } finally {
      sessionStore.clear();
      setUser(null);
    }
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ user, signIn, signOut, pending, locale, setLocale }),
    [user, signIn, signOut, pending, locale, setLocale],
  );

  /**
   * `LocaleProvider` is rendered HERE rather than beside this one in `App`, so
   * that the language and the session cannot be mounted independently. Every
   * screen is inside a session; every screen therefore has a language, and no
   * future route can forget to be wrapped.
   */
  return (
    <SessionContext.Provider value={value}>
      <LocaleProvider locale={locale}>{children}</LocaleProvider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within <SessionProvider>');
  return ctx;
}

/**
 * The session if there is one, `null` if this tree has no provider.
 *
 * `useSession` throws on purpose — a screen that reads the signed-in user
 * outside a provider is a wiring bug and should say so loudly. This is for the
 * one caller that must not: `usePermissions`, which decides whether to *disable*
 * a control and is explicitly designed to fail open when it cannot find out who
 * is signed in. Making that hook throw would mean every component touching a
 * permission — which is most of them — could no longer be rendered in isolation,
 * and would trade a real property (server-side enforcement) for a test harness
 * requirement.
 */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}
